import type { ReactElement } from "react";

import { NewTabIcon } from "../../components/new-tab-icon.js";
import { InvoiceIssuer, InvoiceTable, type InvoiceIssuerFields, type InvoiceTotals } from "../invoices/invoice-page.js";

/**
 * 作成前の見積り（<c>GET /api/estimate</c>。<b>保存しない</b>）。明細と合計の欄は請求書と同じ。
 * 番号・日付・発行元は作成したときに決まるので、ここには無い。
 */
export interface EstimatePreview extends InvoiceTotals {
    readonly summaryLabel: string;
    readonly totalLabel: string;
}

/**
 * 保存した見積書 1 通（<c>POST /api/estimate</c> の応答と、管理センターの <c>GET /api/admin-center/estimates/{id}</c> の <c>estimate</c>）。
 * 明細と発行元の欄は請求書と同じ。
 */
export interface Estimate extends EstimatePreview, InvoiceIssuerFields {
    /** 「Q261012-000123」（Q＋発行日＋保存した番号）。 */
    readonly estimateNumber: string;
    readonly issueDateLabel: string;
    readonly validUntilLabel: string;
    /** 宛名（前後の空白はサーバが落としてある）。「○○ 御中」と出す。 */
    readonly addressee: string;
    /** 作成した日に申し込んだときの無料期間。無料のプランでは null。 */
    readonly trialLabel: string | null;
    /** 「初回のお支払い 2026年12月01日」。無料のプランでは null。 */
    readonly payDateLabel: string | null;
}

/**
 * 月額の見積りであることと、作成した日に申し込んだときの無料期間。<b>文言は新規申し込みの確認と同じ</b>
 * （月額（税込）・無料でお使いいただける期間・契約の更新。tenants/confirmation-view.tsx）。
 */
function EstimateNotes({ estimate }: { readonly estimate: Estimate }): ReactElement {
    return (
        <dl className="mb-8 grid grid-cols-[10rem_1fr] gap-y-2.5 text-sm print:mb-5" data-estimate-notes="">
            <dt className="text-slate-500">月額（税込）</dt>
            <dd>
                {estimate.totalLabel}（1 か月分）
                {estimate.payDateLabel === null ? null : <span className="text-slate-500">（見積書の発行日にお申し込みの場合 {estimate.payDateLabel}）</span>}
            </dd>
            {estimate.trialLabel === null
                ? null
                : (
                    <>
                        <dt className="text-slate-500">無料でお使いいただける期間</dt>
                        <dd>
                            {estimate.trialLabel}
                            <span className="text-slate-500">
                                （見積書の発行日に、新規にお申込みを頂いた場合の無料期間です。この期間は料金がかかりません。
                                詳しくは<a href="/Home/Faq#trial-period" target="_blank" rel="noopener" className="inline-link">よくあるご質問<NewTabIcon /></a>の、無料期間についての質問をご覧ください。）
                            </span>
                        </dd>
                    </>
                )}
            {estimate.payDateLabel === null
                ? null
                : (
                    <>
                        <dt className="text-slate-500">契約の更新</dt>
                        <dd>1 か月ごとに自動で更新されます。解約のお手続きをされるまで、毎月の月額料金がかかります。</dd>
                    </>
                )}
        </dl>
    );
}

/**
 * 見積書の紙面。<b>見積書（作成した後）と管理センターの見積書 1 通で同じものを描く</b>——片方だけ見た目を変えない。
 * 見た目と印刷のしかたは請求書（<c>invoices/invoice-page.tsx</c>）と同じで、明細の表と発行元は請求書の部品を使う。
 */
export function EstimateSheet({ estimate }: { readonly estimate: Estimate }): ReactElement {
    return (
        <div className="panel print-sheet">
            <div className="mb-8 flex flex-wrap items-start justify-between gap-4 print:mb-5">
                <h1 className="page-title">見積書</h1>
                <dl className="grid min-w-64 grid-cols-[7rem_1fr] gap-y-1 text-sm">
                    <dt className="text-slate-500">見積番号</dt><dd>{estimate.estimateNumber}</dd>
                    <dt className="text-slate-500">発行日</dt><dd>{estimate.issueDateLabel}</dd>
                    <dt className="text-slate-500">有効期限</dt><dd>{estimate.validUntilLabel}</dd>
                </dl>
            </div>

            <div className="mb-8 print:mb-5">
                <div className="border-b border-slate-300 pb-2 text-lg font-semibold" data-estimate-addressee="">
                    {`${estimate.addressee} 御中`}
                </div>
                <div className="mt-1 text-sm text-slate-500">{estimate.summaryLabel}</div>
            </div>

            <div className="mb-8 print:mb-5">
                <span className="text-sm text-slate-500">お見積金額（税込・月額）</span>
                <div className="text-2xl font-bold">{estimate.totalLabel}</div>
            </div>

            <InvoiceTable invoice={estimate} />

            <EstimateNotes estimate={estimate} />

            <InvoiceIssuer issuer={estimate} />
        </div>
    );
}
